import { useSyncExternalStore } from "react";

export const PLATFORM_NAVIGATE_EVENT = "platform:navigate";

export interface NavigateOptions {
  readonly replace?: boolean;
}

function pathnameSnapshot(): string {
  return typeof window === "undefined" ? "/" : window.location.pathname;
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(PLATFORM_NAVIGATE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(PLATFORM_NAVIGATE_EVENT, onStoreChange);
  };
}

/**
 * Updates browser history for an in-app route and notifies every pathname
 * consumer without taking a dependency on a routing package.
 */
export function navigate(path: string, { replace = false }: NavigateOptions = {}): void {
  if (typeof window === "undefined") return;
  const target = new URL(path, window.location.origin);
  if (target.origin !== window.location.origin) {
    window.location.assign(target.href);
    return;
  }

  const destination = `${target.pathname}${target.search}${target.hash}`;
  if (replace) {
    window.history.replaceState({}, "", destination);
  } else {
    window.history.pushState({}, "", destination);
  }
  window.dispatchEvent(new Event(PLATFORM_NAVIGATE_EVENT));
}

/** Keeps React in sync with browser back/forward and internal navigation. */
export function usePathname(): string {
  return useSyncExternalStore(subscribe, pathnameSnapshot, () => "/");
}
